import { type ReactElement, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { buildUrl, useSite } from '@salesforce/storefront-next-runtime/site-context';
import { preparePrivacyConsentHtml } from '@/lib/parse-privacy-html';
import { useCurrentSiteAndLocaleRef } from '@/hooks/use-current-site-and-locale-ref';
import { cn } from '@/lib/utils';
import type { AppConfig } from '@/types/config';

type PrivacyConsentTextProps = {
    html: string | null | undefined;
    className?: string;
    linkClassName?: string;
};

export function PrivacyConsentText({ html, className, linkClassName }: PrivacyConsentTextProps): ReactElement | null {
    const { t } = useTranslation('common');
    const { site } = useSite();
    const config = useConfig<AppConfig>();
    const { siteRef, localeRef } = useCurrentSiteAndLocaleRef();
    const openInNewTabHintId = useId();

    const preparedHtml = useMemo(() => {
        const trimmed = html?.trim();
        if (!trimmed) {
            return null;
        }

        const prefixHref = site
            ? (path: string) =>
                  buildUrl({
                      to: path,
                      urlConfig: config.url,
                      params: { siteId: siteRef, localeId: localeRef },
                  })
            : undefined;

        return preparePrivacyConsentHtml(trimmed, {
            linkClassName,
            prefixHref,
            openInNewTabDescribedById: openInNewTabHintId,
        });
    }, [html, linkClassName, site, config.url, siteRef, localeRef, openInNewTabHintId]);

    if (!preparedHtml) {
        return null;
    }

    return (
        <span className={cn(className)}>
            {/* eslint-disable-next-line react/no-danger -- CMS-authored HTML from Business Manager content asset */}
            <span dangerouslySetInnerHTML={{ __html: preparedHtml }} />
            <span id={openInNewTabHintId} className="sr-only">
                {t('openInNewTab')}
            </span>
        </span>
    );
}
